import { useState, lazy, Suspense } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  DollarSign, 
  BarChart3, 
  Bot,
  TrendingUp,
  FileText
} from 'lucide-react';
import { FinancialChartWidget } from './FinancialChartWidget';
import { ProposalFunnelWidget } from './ProposalFunnelWidget';
import { DelayIndexWidget } from './DelayIndexWidget';
import { PredictiveAIWidget } from './PredictiveAIWidget';
import { AIAssistantWidget } from './AIAssistantWidget';

interface DashboardTabsProps {
  isLoaded: boolean;
}

const TabSkeleton = () => (
  <div className="space-y-4 p-4">
    <Skeleton className="h-8 w-48" />
    <Skeleton className="h-64 w-full" />
  </div>
);

export const DashboardTabs = ({ isLoaded }: DashboardTabsProps) => {
  const [activeTab, setActiveTab] = useState('financeiro');

  const tabs = [
    { 
      id: 'financeiro', 
      label: 'Financeiro', 
      icon: DollarSign,
      description: 'Receitas, despesas e funil de propostas'
    },
    { 
      id: 'performance', 
      label: 'Performance', 
      icon: BarChart3,
      description: 'Índice de atrasos e previsões'
    },
    { 
      id: 'assistente', 
      label: 'Assistente IA', 
      icon: Bot,
      description: 'Sugestões e insights inteligentes'
    },
  ];

  return (
    <Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b border-border/50 bg-muted/30 px-4 lg:px-6 pt-4">
          <TabsList className="w-full justify-start gap-1 bg-transparent h-auto p-0">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-t-lg rounded-b-none border-b-2 border-transparent data-[state=active]:border-primary px-4 py-3 gap-2 transition-all"
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Tab: Financeiro */}
        <TabsContent value="financeiro" className="mt-0 p-4 lg:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-1">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Fluxo Mensal
              </h3>
              <FinancialChartWidget isLoaded={isLoaded} compact />
            </div>
            <div className="lg:col-span-1">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Funil de Propostas
              </h3>
              <ProposalFunnelWidget />
            </div>
          </div>
        </TabsContent>

        {/* Tab: Performance */}
        <TabsContent value="performance" className="mt-0 p-4 lg:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Índice de Atrasos
              </h3>
              <DelayIndexWidget isLoaded={isLoaded} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Previsão IA
              </h3>
              <PredictiveAIWidget isLoaded={isLoaded} />
            </div>
          </div>
        </TabsContent>

        {/* Tab: Assistente */}
        <TabsContent value="assistente" className="mt-0 p-4 lg:p-6">
          <AIAssistantWidget isLoaded={isLoaded} />
        </TabsContent>
      </Tabs>
    </Card>
  );
};
